import { AnyAction } from 'redux';
import {
  DraftConfiguration,
  DraftConfigurationSpecialTextStyle,
  DraftConfigurationTextStyle,
  DraftConfigurationWithText,
  Point,
} from '@/types';
import { round } from '@/utils/math/round';
import { getBendingTextSizeForStyle } from '@/utils/text/getBendingTextSizeForStyle';
import { getTextSizeForStyle } from '@/utils/text/getTextSizeForStyle';
import { getCurrentSizeOfConfiguration } from '@/model/configuration/getCurrentSizeOfConfiguration';
import { getOffsetAfterResize } from '@/model/configuration/getOffsetAfterResize';
import {
  isDraftBendingTextConfiguration,
  isDraftSpecialTextConfiguration,
  isDraftTextOrPlaceHolderTextConfiguration,
} from '@/model/configuration/isDraftConfigurationType';
import { scaleSize } from '@/model/configuration/scaleSize';
import { fetchSpecialFontTask, getSpecialFontTask } from '@/model/font/fetchSpecialFontTask';
import { isEnlargeable } from '@/model/printType/isEnlargeable';
import { isPrintColorColorSpace } from '@/model/printType/isPrintColorColorSpace';
import { ensureFontIsAddedToDocumentAction } from '@/redux/core/actions/ensureFontIsAddedToDocumentAction';
import { Thunk } from '@/redux/types';
import { setSpecialTextStyleAction } from '@/components/draft/draftState/actions/setSpecialTextStyleAction';
import { setTextStyleAction } from '@/components/draft/draftState/actions/setTextStyleAction';

export const setConfigurationTextStyle =
  (
    configuration: DraftConfigurationWithText,
    nextTextStyle: DraftConfigurationTextStyle,
    offset?: Point
  ): Thunk<Promise<void>, AnyAction> =>
  async (dispatch, getState) => {
    const { draftDispatch } = getState();
    const printType = configuration.printType;

    const nextFont = nextTextStyle.font;
    if (!nextFont.supportedGlyphs) {
      nextTextStyle.font = await dispatch(ensureFontIsAddedToDocumentAction(nextTextStyle.font));
    }

    const printArea = configuration.printArea;
    const configurationOffset = configuration.offset;
    const configurationSize = configuration.size;
    const configurationText = configuration.text;
    const configurationTextStyle = configurationText.style;
    const configurationFont = configurationTextStyle.font;

    if (isDraftBendingTextConfiguration(configuration)) {
      let scale = configuration.scale;
      const nextFontSize = nextTextStyle.fontSize * scale;
      let scaleFactor = 1;

      if (
        configurationFont.identifier !== nextFont.identifier &&
        isPrintColorColorSpace(printType) &&
        nextFontSize < nextFont.minimalSize
      ) {
        // the new font has a larger minimumSize and we need to adjust the scale.
        // the new scale needs to have two digits max as the bent text design will get created with this value
        // as minimum scale and our systems use an int as minimum scale
        scaleFactor = round(nextFont.minimalSize / nextFontSize, 2);
        scale = round(scale * scaleFactor, 2);
      }

      draftDispatch(
        setTextStyleAction(
          configuration,
          nextTextStyle,
          getOffsetAfterResize(
            configurationOffset,
            getCurrentSizeOfConfiguration(configuration),
            scaleSize(getBendingTextSizeForStyle(nextTextStyle, configurationText.content), scale),
            printArea
          ),
          { scale }
        )
      );
    }

    if (isDraftTextOrPlaceHolderTextConfiguration(configuration)) {
      let newOffset = offset;

      // If the font size changes adjust the offset to keep the text center where it currently is
      // This is only done if no new offset is provided as otherwise we would override adjustments from
      // TextConfigurationHandler where values are slightly different and would cause the text to jump
      // after scaling.
      if (configurationTextStyle.fontSize !== nextTextStyle.fontSize && !offset) {
        newOffset = getOffsetAfterResize(
          configurationOffset,
          configurationSize,
          getTextSizeForStyle(nextTextStyle, configurationText.content),
          printArea
        );
      }

      // If the font changes, adjust the font size so that new font fits into the current width
      if (configurationFont.identifier !== nextFont.identifier) {
        const newTextSize = getTextSizeForStyle(nextTextStyle, configuration.text.content);
        nextTextStyle.fontSize *= configurationSize.width / newTextSize.width;

        if (isEnlargeable(printType) && nextTextStyle.fontSize < nextFont.minimalSize) {
          nextTextStyle.fontSize = nextFont.minimalSize;
        }
      }

      draftDispatch(setTextStyleAction(configuration, nextTextStyle, newOffset));
    }
  };

export const setConfigurationSpecialTextStyle =
  (
    configuration: DraftConfiguration,
    style: DraftConfigurationSpecialTextStyle
  ): Thunk<Promise<void>, AnyAction> =>
  async (dispatch, getState) => {
    if (isDraftSpecialTextConfiguration(configuration)) {
      const fontId = style.font.id;
      const textAlign = style.textAlign;
      const textContent = configuration.text.content;
      const specialFontTask = getSpecialFontTask(fontId, textAlign, textContent);

      getState().draftDispatch(setSpecialTextStyleAction(configuration, style, specialFontTask));

      if (!specialFontTask) {
        await dispatch(fetchSpecialFontTask(fontId, textAlign, textContent, configuration)).catch(
          () => {
            /* ignore failed fetchSpecialFontTask */
          }
        );
      }

      return;
    }
  };
